import React from "react";
import { Box, Card, CardContent, CardHeader } from "@mui/material";
import SocialDistributionGraph from "visual-components/analytics/campaign/social-distribution-graph";
import AnalyticsHeader from "visual-components/analyticsDashboard/analytics-header";
import ChartOption from "visual-components/analyticsDashboard/overviewCard/chart-option";
import GraphEmptyState from "visual-components/util/graphs/empty-state";
import { getChartColor } from "./chartColors";

/**
 * Social Distribution Card Component
 * Displays social actions breakdown by platform in a stacked bar chart
 * @param {Object} props
 * @param {Array} props.socialItems - Array of social items with data
 * @param {Function} props.toggleItem - Function to toggle item visibility
 * @param {Object} props.hideMap - Map of hidden items
 * @param {Array<{value: string, label: string}>} [props.options] - Dropdown options
 * @param {string} [props.value] - Selected dropdown value
 * @param {Function} [props.onChange] - Dropdown change handler
 * @param {boolean} [props.isSolo] - Whether to hide dropdown (solo campaign)
 * @returns {import("react").ReactElement}
 */
export default function SocialDistributionCard(props) {
  const { socialItems, toggleItem, hideMap, options, value, onChange, isSolo } = props;

  const brandOptions = socialItems
    ? socialItems.map((item, index) => (
        <ChartOption
          {...item}
          key={index}
          color={getChartColor(index)}
          onClick={toggleItem}
          selected={!hideMap[item.name]}
        />
      ))
    : [];

  const hasData = socialItems && socialItems.some((el) => el.data.some((num) => num > 0));

  return (
    <Card sx={{ height: 500 }}>
      <CardHeader
        title={
          <AnalyticsHeader
            title="Social Action Type"
            tooltipText="The Social Action breaks down the different types of social actions (like, follow, download) that appear in the popup after people enter their email on the landing page. It shows the cumulative total of clicks each action type received and how many clicks each brand contributed to that total. When multiple brands promote the same kind of action, their clicks are both added into the one special action type. For example, if Brand A and Brand B both ask for Facebook likes, all clicks to either one are grouped together under a single 'Facebook Likes' category."
            showSelect={!isSolo}
            selectProps={{ value, options, onChange }}
          />
        }
      />
      <CardContent>
        {hasData ? (
          <>
            <SocialDistributionGraph {...props} />
            <Box sx={{ pl: 3, mt: 1.5 }}>{brandOptions}</Box>
          </>
        ) : (
          <GraphEmptyState
            text="Social Actions is a powerful feature reserved for Essential and Premium DojoMojo users."
            learnMore="https://help.dojomojo.com/hc/en-us/articles/230426167-Add-Partner-Social-Actions-"
            bigCard
            bigText
          />
        )}
      </CardContent>
    </Card>
  );
}
